/**
 * Costs Page - Pricepoint Management by Type
 *
 * Follows the DataPage pattern with:
 * - ModuleNavSidebar for pricepoint type selection
 * - Three-tab structure: IMPORT | [TYPE] | HISTORY
 * - Dynamic middle tab label based on selected type
 *
 * State flow:
 * 1. usePricepointTypes fetches types for page='cost'
 * 2. NavItems built from types, NavSections from sections
 * 3. Auto-select first type on load
 * 4. User selects type in sidebar -> setSelectedType
 * 5. CostsManageTab receives selectedType prop
 * 6. MetadataTable filters by pricepointTypeId
 *
 * Route: /costs
 */

import { useState, useMemo, useEffect } from 'react'
import { Tabs, Spin, Alert, Empty } from 'antd'
import {
  DollarOutlined,
  TagOutlined,
  SafetyOutlined,
  ProfileOutlined,
  UserOutlined,
  ProjectOutlined,
  QuestionOutlined,
} from '@ant-design/icons'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { NoOrgSelectedPrompt } from '@/components/NoOrgSelectedPrompt'
import { ModuleNavSidebar } from '@/components/navigation'
import { CostsManageTab, CostsHistoryTab, CostsImportTab } from './components'
import { usePricepointTypes } from './hooks'
import type { NavItem, NavSection } from '@/types/navigation.types'
import type { PricepointTypeKey } from '@/types/pricepoint.types'

// CSS for sidebar layout
import '@/components/navigation/ModuleNavSidebar.css'

const STORAGE_KEY_TAB = 'costsPage.activeTab'
const STORAGE_KEY_TYPE = 'costsPage.selectedType'

type MainTab = 'import' | 'manage' | 'history'

/**
 * Map Ant Design icon names (from API) to React components
 */
const ICON_MAP: Record<string, React.ReactNode> = {
  DollarOutlined: <DollarOutlined />,
  TagOutlined: <TagOutlined />,
  SafetyOutlined: <SafetyOutlined />,
  ProfileOutlined: <ProfileOutlined />,
  UserOutlined: <UserOutlined />,
  ProjectOutlined: <ProjectOutlined />,
}

function getIconByName(iconName: string): React.ReactNode {
  return ICON_MAP[iconName] || <QuestionOutlined />
}

export default function CostsPage() {
  const { data: user, isLoading: isUserLoading } = useCurrentUser()
  const hasOrgContext = !!user?.currentOrganization

  // Fetch pricepoint types for cost page
  const {
    data: pricepointTypesData,
    isLoading: isTypesLoading,
    error: typesError,
  } = usePricepointTypes({ page: 'cost' })

  // State: selected pricepoint type
  const [selectedType, setSelectedType] = useState<PricepointTypeKey | null>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY_TYPE)
      if (saved) {
        return saved as PricepointTypeKey
      }
    } catch {
      // Ignore localStorage errors
    }
    return null
  })

  // State: active tab
  const [activeTab, setActiveTab] = useState<MainTab>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY_TAB)
      if (saved === 'import' || saved === 'manage' || saved === 'history') {
        return saved
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'manage'
  })

  // Auto-select first type when types load (if none selected)
  useEffect(() => {
    if (pricepointTypesData?.types && pricepointTypesData.types.length > 0 && !selectedType) {
      const firstType = pricepointTypesData.types[0].key
      setSelectedType(firstType)
      try {
        localStorage.setItem(STORAGE_KEY_TYPE, firstType)
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [pricepointTypesData?.types, selectedType])

  // Build nav items from pricepoint types
  const navItems: NavItem[] = useMemo(() => {
    if (!pricepointTypesData?.types) return []

    return pricepointTypesData.types.map((type) => ({
      key: type.key,
      label: type.label,
      icon: getIconByName(type.icon),
      section: type.section,
      tooltip: type.description,
    }))
  }, [pricepointTypesData?.types])

  // Build nav sections
  const navSections: NavSection[] = useMemo(() => {
    if (!pricepointTypesData?.sections) return []

    return pricepointTypesData.sections.map((section) => ({
      key: section.key,
      label: section.label,
      order: section.order,
    }))
  }, [pricepointTypesData?.sections])

  // Dynamic tab label from selected type
  const manageTabLabel = useMemo(() => {
    if (!selectedType || !pricepointTypesData?.types) return 'PRICEPOINTS'

    const selectedTypeData = pricepointTypesData.types.find((t) => t.key === selectedType)
    return selectedTypeData?.label?.toUpperCase() || 'PRICEPOINTS'
  }, [selectedType, pricepointTypesData?.types])

  // Handle tab change
  const handleTabChange = (key: string) => {
    const newTab = key as MainTab
    setActiveTab(newTab)
    try {
      localStorage.setItem(STORAGE_KEY_TAB, newTab)
    } catch {
      // Ignore localStorage errors
    }
  }

  // Handle sidebar selection
  const handleTypeSelect = (key: string) => {
    setSelectedType(key as PricepointTypeKey)
    try {
      localStorage.setItem(STORAGE_KEY_TYPE, key)
    } catch {
      // Ignore localStorage errors
    }
  }

  // Loading state
  if (isUserLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  // No organization selected - show friendly prompt
  if (!hasOrgContext) {
    return <NoOrgSelectedPrompt featureName="costs" />
  }

  // Loading types
  if (isTypesLoading) {
    return (
      <div className="page-with-sidebar">
        <div style={{ width: 220, background: 'var(--rp-surface-secondary)', minHeight: '100%' }}>
          <Spin style={{ margin: '40px auto', display: 'block' }} />
        </div>
        <div className="page-content">
          <Spin size="large" style={{ marginTop: 64 }} />
        </div>
      </div>
    )
  }

  // Error loading types
  if (typesError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Failed to load cost types"
          description={typesError instanceof Error ? typesError.message : 'Unknown error'}
        />
      </div>
    )
  }

  // No types available
  if (!pricepointTypesData?.types || pricepointTypesData.types.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No cost types configured" />
      </div>
    )
  }

  // Tab items configuration
  const tabItems = [
    {
      key: 'import',
      label: 'IMPORT',
      children: <CostsImportTab />,
    },
    {
      key: 'manage',
      label: manageTabLabel,
      children: <CostsManageTab selectedType={selectedType} />,
    },
    {
      key: 'history',
      label: 'HISTORY',
      children: <CostsHistoryTab />,
    },
  ]

  return (
    <div className="page-with-sidebar">
      {/* Module Navigation Sidebar */}
      <ModuleNavSidebar
        items={navItems}
        sections={navSections}
        selectedKey={selectedType || ''}
        onSelect={handleTypeSelect}
        storageKey="costsPage.sidebar"
      />

      {/* Main Content */}
      <div className="page-content">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
        />
      </div>
    </div>
  )
}
